import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { aspNetCultureDetector } from './aspNetCultureDetector';

/**
 * i18next over the JSON locale files the Razor app already ships. Nothing is
 * re-translated.
 *
 * The one wrinkle is the file format. These files are keyed by resource, with
 * every culture inside:
 *
 *   { "Auth.Login": { "en-US": "Login", "it-IT": "Accedi", ... } }
 *
 * i18next expects one file per language. Rather than convert the files — they
 * are still read by the server-side JSON localizer for the Razor pages that
 * remain — the backend below fetches each file once and pivots it for the
 * active language.
 */

/** Cultures from `Application:SupportedCultures`. */
export const SUPPORTED_CULTURES = ['en-GB', 'it-IT', 'fr-FR', 'es-ES'] as const;

/** `Application:DefaultCulture`. */
export const DEFAULT_CULTURE = 'en-GB';

/**
 * The locale files were authored with en-US, while the application is
 * configured for en-GB. Resolution therefore tries the exact culture, then any
 * culture sharing the language, then English, before giving up — otherwise
 * every en-GB user would see raw resource keys.
 */
function resolveText(entry: Record<string, string>, culture: string): string | undefined {
  if (entry[culture]) return entry[culture];

  const language = culture.split('-')[0].toLowerCase();
  const sameLanguage = Object.keys(entry).find((key) => key.split('-')[0].toLowerCase() === language);
  if (sameLanguage) return entry[sameLanguage];

  const english = Object.keys(entry).find((key) => key.toLowerCase().startsWith('en'));
  return english ? entry[english] : undefined;
}

type RawResource = Record<string, Record<string, string> | string>;

function pivot(raw: RawResource, culture: string): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'string') {
      // Already flat; take it as-is.
      result[key] = value;
      continue;
    }

    const text = resolveText(value, culture);
    if (text !== undefined) result[key] = text;
  }

  return result;
}

/**
 * Namespaces served by the SharedUI RCL at /myui/locales, and by MysteryCalls at
 * /mystery/locales. A namespace prefixed `mystery/` is routed to the latter.
 */
export const NAMESPACES = [
  'shared',
  'auth',
  'crud',
  'entities',
  'entities.auditable',
  'menu.dashboard',
  'validation',
  // The Stellantis screens. All five Razor pages injected
  // IStringLocalizer<DealerList>, so their strings live in one resource file.
  'mystery/dealerlist',
] as const;

function localePath(namespace: string): string {
  return namespace.startsWith('mystery/')
    ? `/mystery/locales/${namespace.slice('mystery/'.length)}.json`
    : `/myui/locales/${namespace}.json`;
}

export async function initI18n() {
  const detector = new LanguageDetector();
  detector.addDetector(aspNetCultureDetector);

  await i18next
    .use(HttpBackend)
    .use(detector)
    .use(initReactI18next)
    .init({
      supportedLngs: [...SUPPORTED_CULTURES],
      fallbackLng: DEFAULT_CULTURE,
      // These files are region-specific, so en-GB must not be truncated to en.
      load: 'currentOnly',
      ns: [...NAMESPACES],
      defaultNS: 'shared',
      // Resource ids contain dots ("Auth.Login") and colons appear in no key, so
      // both separators are disabled to stop i18next reading them as structure.
      keySeparator: false,
      nsSeparator: ':',
      interpolation: { escapeValue: false },
      detection: {
        // aspNetCulture reads the same cookie the Razor pages set, so a language
        // chosen on either side carries over while both are reachable.
        order: ['querystring', 'aspNetCulture', 'localStorage', 'navigator'],
        lookupQuerystring: 'lang',
        caches: ['localStorage'],
      },
      backend: {
        loadPath: (_languages: string[], namespaces: string[]) => localePath(namespaces[0]),
        parse: (data: string, languages?: string | string[]) => {
          const culture = Array.isArray(languages) ? languages[0] : (languages ?? DEFAULT_CULTURE);
          return pivot(JSON.parse(data) as RawResource, culture);
        },
      },
      react: {
        useSuspense: false,
      },
    });

  return i18next;
}

export default i18next;
